import { useCallback } from 'react';
import axios from 'axios';

export const useQuickCutFlow = ({
  BACKEND_URL,
  selectedService,
  quickCutWindowStart,
  quickCutSearchAttempts,
  setQuickCutWindowStart,
  setQuickCutSearchAttempts,
  setShowNoBarbersModal,
  getQuickCutResponseWindowSeconds,
  preferredTime,
  setMatchedBarber,
  setQuickCutStatus,
  setSearchTimeLeft,
  setError,
  currentQuickCutRequestRef,
  quickCutTimerRef,
  quickCutMatchRef,
  setCurrentQuickCutRequestId,
  setSuccess,
  showNotification,
  priceLimit,
  maxDistance,
  serviceLocation,
  user,
  loadClientAcceptedQuickCuts
}) => {
  const handleQuickSearch = useCallback(async ({ isRetry = false } = {}) => {
    if (!selectedService) {
      setError('Por favor selecciona un servicio');
      return;
    }

    const now = Date.now();
    const fifteenMinutes = 15 * 60 * 1000;
    const windowStart = quickCutWindowStart && (now - quickCutWindowStart) < fifteenMinutes
      ? quickCutWindowStart
      : now;
    const currentAttempts = windowStart === now ? 0 : quickCutSearchAttempts;

    if (currentAttempts >= 3) {
      setError('Límite alcanzado. Puedes intentar nuevamente en unos minutos.');
      showNotification('Límite alcanzado', 'Máximo 3 búsquedas cada 15 minutos', 'destructive');
      return;
    }

    setQuickCutWindowStart(windowStart);
    setQuickCutSearchAttempts(currentAttempts + 1);
    setShowNoBarbersModal(false);

    const responseWindowSeconds = getQuickCutResponseWindowSeconds(preferredTime);
    setMatchedBarber(null);
    setQuickCutStatus('searching');
    setSearchTimeLeft(responseWindowSeconds);
    setError('');

    if (currentQuickCutRequestRef.current) {
      try {
        const token = localStorage.getItem('token');
        await axios.post(`${BACKEND_URL}/api/quick-cuts/${currentQuickCutRequestRef.current}/cancel`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) {
        // ignore
      }
    }

    let matchInterval = null;

    if (quickCutTimerRef.current) {
      clearInterval(quickCutTimerRef.current);
    }
    if (quickCutMatchRef.current) {
      clearInterval(quickCutMatchRef.current);
    }

    const timer = setInterval(() => {
      setSearchTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          if (matchInterval) {
            clearInterval(matchInterval);
          }
          setQuickCutStatus('idle');
          setError('Ninguna de las barberías está disponible en este momento.');
          setShowNoBarbersModal(true);
          if (currentQuickCutRequestRef.current) {
            try {
              const token = localStorage.getItem('token');
              axios.post(`${BACKEND_URL}/api/quick-cuts/${currentQuickCutRequestRef.current}/cancel`, {}, {
                headers: { Authorization: `Bearer ${token}` }
              });
            } catch (error) {
              // ignore
            }
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    quickCutTimerRef.current = timer;

    matchInterval = setInterval(async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/quick-cuts/check-match`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });

        if (response.data.matched && response.data.barber?.quick_cut_id === currentQuickCutRequestRef.current) {
          clearInterval(timer);
          clearInterval(matchInterval);
          setMatchedBarber(response.data.barber);
          setQuickCutStatus('matched');
          setSuccess('¡Barbero encontrado! Revisa los detalles.');
          setQuickCutSearchAttempts(0);
          setQuickCutWindowStart(null);
        }
      } catch (error) {
        console.log('Checking for matches...');
      }
    }, 3000);
    quickCutMatchRef.current = matchInterval;

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Debes iniciar sesión para solicitar un corte rápido');
        clearInterval(timer);
        clearInterval(matchInterval);
        setQuickCutStatus('idle');
        return;
      }

      const response = await axios.post(`${BACKEND_URL}/api/quick-cuts/request`, {
        service: selectedService,
        max_price: priceLimit[0],
        max_distance: maxDistance[0],
        service_location: serviceLocation,
        preferred_time: preferredTime,
        address: user?.address || 'No especificada',
        lat: 0,
        lng: 0
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const requestId = response.data.id || response.data.quick_cut_id || null;
      setCurrentQuickCutRequestId(requestId);
      currentQuickCutRequestRef.current = requestId;
      showNotification('Solicitud Enviada', 'Tu solicitud fue enviada a todos los barberos disponibles', 'success');
      setSearchTimeLeft(responseWindowSeconds);
    } catch (error) {
      clearInterval(timer);
      clearInterval(matchInterval);
      setQuickCutStatus('idle');
      const status = error.response?.status;
      if (status === 409) {
        setError('Ninguna de las barberías está disponible en este momento.');
        setShowNoBarbersModal(true);
      } else {
        setShowNoBarbersModal(false);
      }
      console.error('Quick search error:', error);
      const errorMsg = error.response?.data?.detail || 'Error al enviar solicitud';
      showNotification('Error', errorMsg, 'destructive');
    }
  }, [
    BACKEND_URL,
    selectedService,
    quickCutWindowStart,
    quickCutSearchAttempts,
    setQuickCutWindowStart,
    setQuickCutSearchAttempts,
    setShowNoBarbersModal,
    getQuickCutResponseWindowSeconds,
    preferredTime,
    setMatchedBarber,
    setQuickCutStatus,
    setSearchTimeLeft,
    setError,
    currentQuickCutRequestRef,
    quickCutTimerRef,
    quickCutMatchRef,
    setCurrentQuickCutRequestId,
    setSuccess,
    showNotification,
    priceLimit,
    maxDistance,
    serviceLocation,
    user?.address
  ]);

  const handleCancelQuickCutSearch = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (currentQuickCutRequestRef.current) {
        await axios.post(`${BACKEND_URL}/api/quick-cuts/${currentQuickCutRequestRef.current}/cancel`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (error) {
      console.error('Error canceling quick cut:', error);
    } finally {
      if (quickCutTimerRef.current) {
        clearInterval(quickCutTimerRef.current);
      }
      if (quickCutMatchRef.current) {
        clearInterval(quickCutMatchRef.current);
      }
      setQuickCutStatus('idle');
      setMatchedBarber(null);
      setCurrentQuickCutRequestId(null);
      currentQuickCutRequestRef.current = null;
      showNotification('Solicitud cancelada', 'Tu solicitud fue cancelada', 'info');
      loadClientAcceptedQuickCuts?.();
    }
  }, [
    BACKEND_URL,
    currentQuickCutRequestRef,
    loadClientAcceptedQuickCuts,
    quickCutMatchRef,
    quickCutTimerRef,
    setCurrentQuickCutRequestId,
    setMatchedBarber,
    setQuickCutStatus,
    showNotification
  ]);

  return { handleQuickSearch, handleCancelQuickCutSearch };
};
